import ExpandMore from '@mui/icons-material/ExpandMore'
import { formatApiDate } from '~/shared/lib/date'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import { FC, useState } from 'react'
import { CarePlanItem, CarePlanApiResponse, CategorizedMeasureCollection } from './carePlanTypes'
import type { Provider } from '~/entities/provider'
import type { NotificationPayload } from '~/shared/notifications/actions'
import { getUserById, getUserName } from '~/shared/lib/names'
import { carePlanAction, carePlan as carePlanApi } from '@fireflyhealth/api'
import { purple, yellow } from '@mui/material/colors'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { required } from '~/shared/lib/required'

const NewCarePlanHeader: FC<{
  carePlan: CarePlanItem
  providers: Provider[]
  openModal: (carePlan: CarePlanItem | null, editClick?: boolean) => void
  setModalCarePlanAction: (
    value: {
      carePlan: CarePlanItem
      action: string
      onCarePlanActionChange: (action: string) => void
    } | null,
  ) => void
  onCareplanCreateOrUpdate: (carePlan: CarePlanItem | CarePlanApiResponse) => void
  dispatchNotification: (notification: NotificationPayload) => void
  categorizedMeasureCollection: CategorizedMeasureCollection
}> = props => {
  const {
    carePlan,
    providers,
    openModal,
    setModalCarePlanAction,
    onCareplanCreateOrUpdate,
    dispatchNotification,
    categorizedMeasureCollection,
  } = props

  const [anchorEl, setAnchorEl] = useState<Element | null>(null)

  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  // Determine visibility for display using visible_to_patient field
  const isVisibleToPatient = carePlan.visible_to_patient ?? false

  const renderHistory = () => {
    if (carePlan.is_case_based_care_plan) return 'Updated Nightly'
    /**
     * Shows a different message depending on whether it's been updated.
     */
    const provider = carePlan.updated_by ? getUserById(providers, carePlan.updated_by) : null
    const createdAt = formatApiDate(carePlan.created_at, 'MMM. d, yyyy') ?? ''
    const updatedAt = formatApiDate(carePlan.updated_at, 'MMM. d, yyyy') ?? ''
    let content = `Created ${createdAt}`
    if (provider !== null) content += ` · Last edited ${updatedAt} by ${getUserName(provider)}`
    return content
  }

  const handleOpenCareplan = (_: React.MouseEvent<HTMLElement>, editClick = false) => {
    setAnchorEl(null)
    openModal(carePlan, editClick)
  }

  const onCarePlanActionChange = async (action: string) => {
    setIsUpdating(true)
    try {
      // Status transitions don't change visibility here — a draft is always
      // hidden, and the backend preserves visible_to_patient across active/
      // inactive transitions. Visibility is controlled by the dedicated
      // hide/show menu actions (toggleVisibility) and the modal flow.
      const { data: apiResponse } = await carePlanAction.partialUpdate(
        { id: carePlan.id },
        {
          data: {
            action: action,
            tasks: (carePlan.tasks ?? []).map(task => ({
              id: task.id,
              is_complete: task.is_complete ?? false,
            })),
            cases: (carePlan.cases ?? []).map(c => ({ id: c.id })),
          },
        },
      )
      onCareplanCreateOrUpdate(apiResponse)
    } catch (__) {
      dispatchNotification({
        variant: 'error',
        message: 'Something went wrong. Please try again',
      })
      const { data: apiResponse } = await carePlanApi.retrieve({ id: carePlan.id })
      onCareplanCreateOrUpdate(apiResponse)
    }
    setIsUpdating(false)
  }

  const toggleVisibility = async (makeVisible: boolean) => {
    setIsUpdating(true)
    setAnchorEl(null)
    try {
      // Use carePlanAction endpoint which accepts visible_to_patient as writable
      const { data: apiResponse } = await carePlanAction.partialUpdate(
        { id: carePlan.id },
        {
          data: {
            visible_to_patient: makeVisible,
            tasks: (carePlan.tasks ?? []).map(task => ({
              id: task.id,
              is_complete: task.is_complete ?? false,
            })),
            cases: (carePlan.cases ?? []).map(c => ({ id: c.id })),
          },
        },
      )
      onCareplanCreateOrUpdate(apiResponse)
    } catch (__) {
      dispatchNotification({
        variant: 'error',
        message: 'Something went wrong. Please try again',
      })
      const { data: apiResponse } = await carePlanApi.retrieve({ id: carePlan.id })
      onCareplanCreateOrUpdate(apiResponse)
    }
    setIsUpdating(false)
  }

  const handleActionChange = (action: { dest: string; trigger: string }) => {
    const destCategory = carePlan.states_with_categories?.find(
      stateWithCategory => stateWithCategory.state.name === action.dest,
    )
    // When a care plan is marked complete, then all the pending tasks gets deleted
    // and all the pending cases will get delinked from the care plan.
    // So before completing a care plan make sure that there are no pending tasks or cases.
    // If there are pending tasks or cases, then show the list to the user.
    // Only when the user acknowledge it, then mark the care plan as complete.
    if (
      destCategory &&
      (destCategory.category === 'complete' || destCategory.category === 'deferred') &&
      ((carePlan.cases ?? []).length > 0 ||
        (carePlan.tasks ?? []).filter(task => !task.is_complete).length > 0)
    ) {
      // Close the menu before opening the confirmation modal. Both the menu and
      // the modal install their own focus trap on `document`; leaving the menu
      // open would keep two traps active at once, and they fight each other for
      // focus indefinitely.
      setAnchorEl(null)
      setModalCarePlanAction({
        carePlan: carePlan,
        action: action.trigger,
        onCarePlanActionChange: action =>
          fireAndForget('[CarePlan] onCarePlanActionChange', () => onCarePlanActionChange(action)),
      })
    } else {
      void onCarePlanActionChange(action.trigger)
    }
  }

  return (
    <AccordionSummary
      aria-label="Expand"
      aria-controls={`categorized-todos-${carePlan.title}-content`}
      id={`categorized-todos-${carePlan.title}-header`}
      style={{ flexGrow: 1 }}
      expandIcon={<ExpandMore />}
      sx={{
        flexDirection: 'row-reverse',
        '.MuiAccordionSummary-expandIconWrapper': {
          transform: 'rotate(-90deg)',
          color: 'grey.400',
        },
        '.MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
          transform: 'rotate(0deg)',
          color: 'grey.400',
        },
      }}
    >
      <Box
        style={{ flexGrow: 1, display: 'flex' }}
        onClick={e => {
          if (!(e.target as HTMLElement).closest('[data-accordion-toggle]')) {
            e.stopPropagation()
            e.preventDefault()
          }
        }}
      >
        <Box style={{ width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Typography
              data-accordion-toggle
              sx={{
                fontWeight: 'bold',
                color: 'text.primary',
                display: 'inline-flex',
                cursor: 'pointer',
              }}
              style={{ marginLeft: 1 }}
            >
              {carePlan.title}
            </Typography>
            {/* Show visibility badge for all care plans */}
            <Box
              sx={{
                alignSelf: 'center',
                display: 'inline-flex',
                flexDirection: 'row',
                marginLeft: 1,
                backgroundColor: isVisibleToPatient ? yellow[100] : purple[50],
                paddingLeft: 1,
                paddingRight: 1,
                paddingTop: 0.2,
                paddingBottom: 0.2,
              }}
            >
              {isVisibleToPatient ? (
                <>
                  <Visibility style={{ fontSize: '1.8rem', marginTop: '0.5' }} />
                  <Typography sx={{ fontSize: '1.4rem', ml: 0.5 }}>{'Member-Facing'}</Typography>
                </>
              ) : (
                <>
                  <VisibilityOff style={{ fontSize: '1.8rem', marginTop: '0.5' }} />
                  <Typography sx={{ fontSize: '1.4rem', ml: 0.5 }}>
                    {'Not visible to member'}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          <Typography data-accordion-toggle sx={{ mt: '0.3rem', fontSize: '1.2rem' }}>
            {renderHistory()}
          </Typography>
        </Box>
        {carePlan.is_case_based_care_plan &&
        carePlan.status == 'draft' &&
        Object.keys(categorizedMeasureCollection).length > 0 ? (
          <Button
            onClick={handleOpenCareplan}
            variant="contained"
            size="small"
            sx={{
              borderRadius: 10,
              fontSize: '1.4rem',
              maxHeight: '75%',
              width: '25%',
              marginRight: 2,
            }}
          >
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>Review and Send</Typography>
          </Button>
        ) : null}
        <Box style={{ flexShrink: 0 }}>
          {/* Always show menu for all care plans */}
          <IconButton
            sx={{ p: 0.5 }}
            onClick={e => {
              setAnchorEl(e.currentTarget)
            }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => {
              setAnchorEl(null)
            }}
            slotProps={{
              list: {
                disablePadding: true,
              },
            }}
          >
            {/* Case-based care plan menu items */}
            {carePlan.is_case_based_care_plan
              ? [
                  <MenuItem
                    key="edit-automatic"
                    onClick={e => {
                      handleOpenCareplan(e, true)
                    }}
                  >
                    Edit
                  </MenuItem>,
                  // Only show visibility toggle for non-draft care plans (draft is always hidden)
                  // Uses handleOpenCareplan to preserve the modal flow for case-based care plans
                  carePlan.status !== 'draft' &&
                    (isVisibleToPatient ? (
                      <MenuItem key="hide-from-member" onClick={handleOpenCareplan}>
                        Hide from member
                      </MenuItem>
                    ) : (
                      <MenuItem key="make-visible" onClick={handleOpenCareplan}>
                        Make visible to member
                      </MenuItem>
                    )),
                ]
              : null}
            {/* Non-case-based care plan menu items */}
            {!carePlan.is_case_based_care_plan
              ? [
                  <MenuItem key="edit" onClick={handleOpenCareplan}>
                    Edit
                  </MenuItem>,
                  // Show visibility toggle for non-draft care plans
                  carePlan.status !== 'draft' &&
                    (isVisibleToPatient ? (
                      <MenuItem
                        key="hide-from-member"
                        onClick={() =>
                          fireAndForget('[CarePlan] hide from member', () =>
                            toggleVisibility(false),
                          )
                        }
                        disabled={isUpdating}
                      >
                        Hide from member
                      </MenuItem>
                    ) : (
                      <MenuItem
                        key="make-visible"
                        onClick={() =>
                          fireAndForget('[CarePlan] make visible', () => toggleVisibility(true))
                        }
                        disabled={isUpdating}
                      >
                        Make visible to member
                      </MenuItem>
                    )),
                  <MenuItem
                    key="toggle-status"
                    onClick={() => {
                      const actionDetails: Record<string, string> = {
                        active: 'inactive',
                        inactive: 'active',
                        draft: 'active',
                      }
                      const status = required(carePlan.status, 'a care plan status')
                      const toggled = required(
                        actionDetails[status],
                        `a status to toggle ${status} to`,
                      )
                      const action = { dest: toggled, source: carePlan.status, trigger: toggled }
                      handleActionChange(action)
                    }}
                    disabled={isUpdating}
                    value={carePlan.status ?? undefined}
                  >
                    {carePlan.status === `active` ? `Mark inactive` : `Mark active`}
                  </MenuItem>,
                ]
              : null}
          </Menu>
        </Box>
      </Box>
    </AccordionSummary>
  )
}

export default NewCarePlanHeader
